// eslint-disable @atlaskit/design-system/no-legacy-icons
import React, { useCallback, useMemo } from 'react';

import { FormattedMessage } from 'react-intl-next';

import { PrimaryDropdownButton } from '@atlaskit/atlassian-navigation';
import ChevronRightIconLegacy from '@atlaskit/icon/glyph/chevron-right';
import ChevronRightIcon from '@atlaskit/icon/utility/chevron-right';
import { ButtonItem } from '@atlaskit/menu';
import { usePeopleAndTeamsPeopleMenuConfiguration } from '@atlassian/people-teams-configuration-client/people-menu';

import { type PeopleMenuProps } from '../../types';
import { messages } from '../i18n';

type PeopleMenuTriggerProps = Pick<
	PeopleMenuProps,
	'customChevronIcon' | 'testId' | 'peopleText'
> & {
	isVisible: boolean;
	isHighlighted?: boolean;
	triggerProps: any;
	isSelected: boolean;
	onClick: () => void;
	onMouseEnter: () => void;
};

export function PeopleMenuTriggerSSR({
	testId,
	peopleText,
}: {
	testId?: string;
	peopleText?: string;
}) {
	const remoteConfig = usePeopleAndTeamsPeopleMenuConfiguration();
	const navLabel = remoteConfig?.navLabel || peopleText;

	return (
		<PrimaryDropdownButton testId={`${testId}-people-primary-button-ssr`}>
			{navLabel ? navLabel : <FormattedMessage {...messages.teams} />}
		</PrimaryDropdownButton>
	);
}

function PeopleMenuTrigger(props: PeopleMenuTriggerProps) {
	const {
		isVisible,
		isHighlighted,
		customChevronIcon,
		triggerProps,
		onClick,
		onMouseEnter,
		testId,
		isSelected,
		peopleText,
	} = props;

	const buttonProps = useMemo(
		() => ({
			isSelected,
			onClick,
			onMouseEnter,
			testId,
		}),
		[isSelected, onClick, onMouseEnter, testId],
	);

	const remoteConfig = usePeopleAndTeamsPeopleMenuConfiguration();
	const navLabel = remoteConfig?.navLabel || peopleText;

	const chevron = customChevronIcon || (
		<ChevronRightIcon
			label="dropdown-chevron"
			spacing="spacious"
			LEGACY_fallbackIcon={ChevronRightIconLegacy}
		/>
	);
	const renderPrimaryDropDownButton = useCallback(() => {
		return (
			<PrimaryDropdownButton isHighlighted={isHighlighted} {...buttonProps} {...triggerProps}>
				{navLabel || <FormattedMessage {...messages.teams} />}
			</PrimaryDropdownButton>
		);
	}, [buttonProps, isHighlighted, navLabel, triggerProps]);

	if (isVisible) {
		return renderPrimaryDropDownButton();
	}

	return (
		<ButtonItem iconAfter={chevron} {...triggerProps} {...buttonProps}>
			{navLabel || <FormattedMessage {...messages.teams} />}
		</ButtonItem>
	);
}

export default PeopleMenuTrigger;
